import { usePromotions } from "./PromotionContext";
import React, { createContext, useState, useEffect, useCallback } from "react";
import { getLegacyProductImageUrls, getProductImage } from "../utils/productImages";
import axiosInstance from "../api/axiosInstance";
import { clearMonobankPaymentDraft } from "../utils/monobankPaymentDraft";

export const CartContext = createContext();

/* 🔑 helper для стабільного ключа */
const normalizeWeight = (weight) => {
  if (!weight) return null;
  return String(weight).trim();
};

const getCartKey = (productCode, weight) =>
  `${productCode}-${normalizeWeight(weight)}`;

const clearStaleMonobankPaymentDraft = () => {
  clearMonobankPaymentDraft({ includePendingOrder: false });
};

export const CartProvider = ({ children }) => {
  const { revision: promotionRevision } = usePromotions();
  const [priceNotice, setPriceNotice] = useState("");
  /* ===============================
     🛒 STATE
  =============================== */
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem("cartItems");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  /* ===============================
     💾 PERSISTENCE
  =============================== */
  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  /* ===============================
     ➕ ADD TO CART
     item має містити totalAvailable
  =============================== */
  // Ліміт позиції оновлюється перед оформленням; сервер перевіряє весь кошик разом.
  const refreshCartAvailability = useCallback(async () => {
    const current = (() => {
      try { return JSON.parse(localStorage.getItem("cartItems") || "[]"); }
      catch { return []; }
    })();
    const codes = [...new Set(current.map((item) => Number(item.productCode)).filter(Number.isFinite))];
    const results = await Promise.allSettled(codes.map((productCode) =>
      axiosInstance.get("/stock-batches/public", { params: { productCode } })));
    const available = new Map();
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        available.set(codes[index], Number(result.value.data?.totalAvailable || 0));
      }
    });
    if (available.size) {
      setCartItems((items) => items.map((item) => available.has(Number(item.productCode))
        ? { ...item, maxAvailable: available.get(Number(item.productCode)) }
        : item));
    }
  }, []);

  const refreshCartPrices = useCallback(async () => {
    let current;
    try { current = JSON.parse(localStorage.getItem("cartItems") || "[]"); } catch { current = []; }
    const productIds = [...new Set(current.map((p) => p.productId || p._id).filter(Boolean))];
    if (!productIds.length) return false;
    const { data } = await axiosInstance.post("/promotions/cart-prices", { productIds });
    const byId = new Map(data.map((p) => [String(p._id), p]));
    const changed = current.some((item) => {
      const p = byId.get(String(item.productId || item._id));
      return !p || Number(item.price) !== Number(p.price) || Number(item.discountPrice || 0) !== Number(p.discountPrice || 0);
    });
    setCartItems((items) => items.map((item) => {
      const p = byId.get(String(item.productId || item._id));
      return p ? { ...item, price: p.price, discountPrice: p.discountPrice, discountSource: p.discountSource } : { ...item, maxAvailable: 0 };
    }));
    if (changed) setPriceNotice("Ціни або доступність товарів у кошику змінилися. Перевірте їх перед підтвердженням замовлення.");
    return changed;
  }, []);
  useEffect(() => { refreshCartPrices().catch(() => undefined); }, [promotionRevision, refreshCartPrices]);

  const addToCart = (item, quantity = 1) => {
    clearStaleMonobankPaymentDraft();
    setCartItems((prevItems) => {
      const normalizedWeight = normalizeWeight(item.weight);
      const key = getCartKey(item.productCode, normalizedWeight);

      const existingItem = prevItems.find(
        (ci) => getCartKey(ci.productCode, ci.weight) === key,
      );

      const maxAvailable = Number(
        item.totalAvailable ?? item.maxAvailable ?? 0,
      );

      const normalizedImages = getLegacyProductImageUrls(item);
      const normalizedImage = getProductImage(item, "thumbnail");

      if (existingItem) {
        return prevItems.map((ci) => {
          if (getCartKey(ci.productCode, ci.weight) !== key) return ci;

          const max =
            item.maxAvailable ??
            item.totalAvailable ??
            ci.maxAvailable ??
            Infinity;

          return {
            ...ci,
            productId: item._id || item.productId || ci.productId,
            productCode: item.productCode || ci.productCode,
            name: item.name || ci.name,
            kind: item.kind || ci.kind,
            manufacturer: item.manufacturer || ci.manufacturer,
            countryOfOrigin: item.countryOfOrigin || ci.countryOfOrigin,
            weight: normalizedWeight || ci.weight,
            price: Number(item.price ?? ci.price),
            discountPrice:
              item.discountPrice !== undefined && item.discountPrice !== null
                ? Number(item.discountPrice)
                : ci.discountPrice,
            maxAvailable: Number.isFinite(max) ? max : ci.maxAvailable,
            images: normalizedImages.length
              ? normalizedImages
              : ci.images || [],
            image: normalizedImage || ci.image || "/images/default-product.jpg",
            slug: item.slug || ci.slug,
            quantity: Math.min(ci.quantity + quantity, max),
          };
        });
      }

      const newItem = {
        productId: item._id || item.productId,
        productCode: item.productCode,
        name: item.name,
        kind: item.kind,
        manufacturer: item.manufacturer || "Unknown",
        countryOfOrigin: item.countryOfOrigin || "",
        weight: normalizedWeight,
        slug: item.slug || "",

        quantity: Math.min(quantity, maxAvailable || quantity),

        price: Number(item.price),
        discountPrice:
          item.discountPrice !== undefined && item.discountPrice !== null
            ? Number(item.discountPrice)
            : null,

        maxAvailable,

        images: normalizedImages,
        image: normalizedImage,
      };

      return [...prevItems, newItem];
    });
  };

  /* ===============================
     ➖ REMOVE FROM CART
  =============================== */
  const removeFromCart = (productCode, weight) => {
    const key = getCartKey(productCode, weight);
    setCartItems((prevItems) => {
      const nextItems = prevItems.filter(
        (item) => getCartKey(item.productCode, item.weight) !== key,
      );

      clearMonobankPaymentDraft({
        includePendingOrder: nextItems.length === 0,
      });

      return nextItems;
    });
  };

  /* ===============================
     🔄 UPDATE QUANTITY (CLAMPED)
  =============================== */
  const updateItemQuantity = (productCode, weight, newQuantity) => {
    const key = getCartKey(productCode, weight);
    clearStaleMonobankPaymentDraft();

    setCartItems((prevItems) =>
      prevItems.map((item) => {
        if (getCartKey(item.productCode, item.weight) !== key) return item;

        const max = item.maxAvailable ?? Infinity;

        return {
          ...item,
          quantity: Math.max(1, Math.min(newQuantity, max)),
        };
      }),
    );
  };

  /* ===============================
     🧹 CLEAR CART
  =============================== */
  const clearCart = () => {
    clearMonobankPaymentDraft();
    setCartItems([]);
    localStorage.removeItem("cartItems");
  };

  /* ===============================
     💰 TOTAL PRICE
  =============================== */
  const getTotalCartPrice = () => {
    return cartItems.reduce((total, item) => {
      const unitPrice =
        item.discountPrice != null ? item.discountPrice : item.price;
      return total + unitPrice * item.quantity;
    }, 0);
  };

  /* ===============================
     📦 PROVIDER
  =============================== */
  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateItemQuantity,
        refreshCartAvailability,
        refreshCartPrices,
        priceNotice,
        clearCart,
        getTotalCartPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
